/**
 * Read delimited text - .csv, .tsv, and the .txt exports that are really one of
 * those.
 *
 * Worth having because CSV is what every other system will hand you. An ERP, a
 * customer's own register, a report someone saved out of a machine: none of
 * them produce .xlsx, and all of them produce this.
 *
 * Follows RFC 4180 on the parts that bite: quoted fields may contain the
 * delimiter, a newline, or a doubled quote meaning one literal quote. A parser
 * that just splits on commas works until the first mould called
 * "BODY, FRONT" and then silently shifts every column after it.
 */
/** Delimiters worth guessing between, in the order they are tried. */
const CANDIDATES = [',', ';', '\t', '|'];
/**
 * Guess the delimiter from the first few lines.
 *
 * Semicolons matter: Excel writes them instead of commas in locales where the
 * comma is the decimal separator, so a European export looks like one column to
 * a comma-only parser.
 *
 * Chosen by which delimiter gives the most *consistent* column count, not the
 * highest - a stray character that appears many times on one line should not
 * win against one that splits every line into the same shape.
 */
function detectDelimiter(sample) {
    const lines = sample.split(/\r?\n/).filter((l) => l.trim()).slice(0, 10);
    if (lines.length === 0)
        return ',';
    let best = ',';
    let bestScore = -1;
    for (const d of CANDIDATES) {
        const counts = lines.map((l) => splitLine(l, d).length);
        const columns = Math.max(...counts);
        if (columns < 2)
            continue;
        const consistent = counts.filter((c) => c === columns).length / counts.length;
        // Consistency first, width second: two delimiters that both split every
        // line cleanly are separated by which finds more columns.
        const score = consistent * 100 + columns;
        if (score > bestScore) {
            bestScore = score;
            best = d;
        }
    }
    return best;
}
/** Split one line, honouring quotes. Used only by the delimiter guess. */
function splitLine(line, delimiter) {
    const out = [];
    let field = '';
    let quoted = false;
    for (let i = 0; i < line.length; i++) {
        const ch = line[i];
        if (quoted) {
            if (ch === '"') {
                if (line[i + 1] === '"') {
                    field += '"';
                    i++;
                }
                else
                    quoted = false;
            }
            else
                field += ch;
        }
        else if (ch === '"')
            quoted = true;
        else if (ch === delimiter) {
            out.push(field);
            field = '';
        }
        else
            field += ch;
    }
    out.push(field);
    return out;
}
/**
 * Full parse, across lines.
 *
 * Separate from splitLine because a quoted field may contain a newline, so rows
 * cannot be found by splitting on line breaks first.
 */
function parseRows(text, delimiter) {
    const rows = [];
    let row = [];
    let field = '';
    let quoted = false;
    for (let i = 0; i < text.length; i++) {
        const ch = text[i];
        if (quoted) {
            if (ch === '"') {
                if (text[i + 1] === '"') {
                    field += '"';
                    i++;
                }
                else
                    quoted = false;
            }
            else
                field += ch;
            continue;
        }
        if (ch === '"') {
            quoted = true;
        }
        else if (ch === delimiter) {
            row.push(field);
            field = '';
        }
        else if (ch === '\n') {
            row.push(field);
            rows.push(row);
            row = [];
            field = '';
        }
        else if (ch === '\r') {
            // Swallowed: \r\n ends the row on the \n, and a lone \r is a Mac-classic
            // line ending that no longer appears in anything Excel writes.
            if (text[i + 1] !== '\n') {
                row.push(field);
                rows.push(row);
                row = [];
                field = '';
            }
        }
        else {
            field += ch;
        }
    }
    if (field !== '' || row.length > 0) {
        row.push(field);
        rows.push(row);
    }
    return rows;
}
/**
 * Text that is really a number, as a number.
 *
 * Deliberately narrow. "24" becomes 24 so a cavity count sorts and compares
 * properly; "1 +1 =2" stays text because it is a description, and "007" stays
 * text because dropping the zeros would change a code. Anything with a currency
 * symbol or a percent sign stays text too - the unit is part of the meaning.
 */
function coerce(raw) {
    const text = raw.trim();
    if (text === '')
        return null;
    if (!/^-?\d+(\.\d+)?$/.test(text))
        return text;
    if (/^0\d/.test(text))
        return text; // leading zero: a code, not a quantity
    const n = Number(text);
    return Number.isFinite(n) ? n : text;
}
export function parseCsv(text, delimiter) {
    // A byte-order mark survives into the first heading and stops it matching.
    const clean = text.charCodeAt(0) === 0xfeff ? text.slice(1) : text;
    const sep = delimiter ?? detectDelimiter(clean.slice(0, 8000));
    const rows = parseRows(clean, sep);
    const grid = rows.map((r) => r.map(coerce));
    const columnCount = grid.reduce((n, r) => Math.max(n, r.length), 0);
    return {
        name: 'Sheet 1',
        rowCount: grid.length,
        columnCount,
        delimiter: sep,
        cell: (row, col) => grid[row - 1]?.[col - 1] ?? null,
    };
}
//# sourceMappingURL=csv.js.map